import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";

//Components
import { Container, Paper } from "@mui/material";
import { Tabs, TabContainer } from "@gpstrackit/gtc-react-controls";

import { NotificationManager } from "Components/Notifications";

//Views
import EventsTab, { EVENTS_LIST_QUERY_KEY } from "./.components/events";
import ClipsTab, { CLIPS_LIST_QUERY_KEY } from "./.components/clips";

//Actions
import { setCurrentTab, clearRedux, catalogLoad } from "Redux/actions";

//Helpers

export const EVENTS_PREFERENCES_KEY = "videoEvents";
const DELAY_TAB_CHANGE = 2500;
const TAB_SLUGS = ["events", "clips", "starred", "deleted"];
const SLUG_TO_TAB_INDEX = { events: 0, clips: 1, starred: 2, deleted: 3 };

const VideoMainView = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const tableViewRef = useRef();
  const tabsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  //Redux States
  const { updateAvailableMediaResponse: updateResponse } = useSelector(
    (state) => state.videoDetailRedux
  );
  const { openAddNotesModal: open, addNotesModalData: data } = useSelector(
    (state) => state.eventsRedux
  );
  const loadingEvents = useSelector((state) => state.eventsRedux.loading);
  // Solo la carga inicial (sin datos): los refetch en segundo plano (tras crear/eliminar o por
  // push notification) no deben bloquear la vista con loading.
  const isFetchingRequestedClips = useIsFetching({
    queryKey: CLIPS_LIST_QUERY_KEY,
    predicate: (query) => query.state.status === "pending",
  }) > 0;
  const isFetchingEvents = useIsFetching({ queryKey: EVENTS_LIST_QUERY_KEY }) > 0;
  const loadingManageCameras = useSelector(
    (state) => state.manageCamerasRedux.loadingCamerasList
  );
  const { currentTab } = useSelector((state) => state.videoMainRedux);
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const loadingExport = useSelector((state) => state.commomRedux.loadingExport);
  const { loading: loadingEventsList } = useSelector(
    (state) => state.videoEventsListRedux
  );

  //Local States
  const [preferences, setPreferences] = useState();

  //Variables
  let loading = loadingEvents || isFetchingRequestedClips || isFetchingEvents || loadingManageCameras || loadingEventsList;

  useEffect(() => {
    if (updateResponse[data?.id] && open) {
      if (updateResponse[data?.id]?.error) {
        NotificationManager.error(
          messages["errorMessage"],
          messages["error"],
          3000,
          null,
          null,
          "warning"
        );
      } else {
        NotificationManager.success(
          messages["videoMainView_noteAdded"],
          messages["success"],
          3000,
          null,
          null,
          "success"
        );
      }
    }
  }, [updateResponse[data?.id], open]);

  useEffect(() => {
    if (exportResult && exportName === "mediaEvents" && !loadingExport) {
      NotificationManager.info(
        messages["exportInProcessBody"],
        messages["exportInProcessTitle"],
        4000,
        null,
        null,
        "info",
        null
      );
      dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }
  }, [exportResult, exportName, loadingExport]);

  const currentTabRef = useRef(currentTab);
  useEffect(() => {
    currentTabRef.current = currentTab;
  }, [currentTab]);

  // SGSD-8636: when landing via deep link on a non-default tab (e.g. ?tab=clips),
  // the first render still has currentTab=0 (Redux's default) while the URL already
  // says otherwise. The effect below (URL -> Redux) corrects currentTab, but the
  // Redux -> URL effect runs in the SAME commit still seeing the old currentTab, and
  // "corrects" the URL back — each one overwrites the other with a value from the
  // previous render batch, causing an infinite ping-pong between both tabs. This flag
  // tells the Redux -> URL effect that the currentTab change it's about to see came
  // from the URL, so there's no need to rewrite it (the URL is already correct).
  const syncingFromUrlRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    const tabIndex = SLUG_TO_TAB_INDEX[tabParam];
    if (tabIndex !== undefined && tabIndex !== currentTabRef.current) {
      syncingFromUrlRef.current = true;
      dispatch(setCurrentTab(tabIndex));
    }
  }, [location.search, dispatch]);

  useEffect(() => {
    tabsRef.current?.setValue(currentTab);

    if (syncingFromUrlRef.current) {
      syncingFromUrlRef.current = false;
      return;
    }

    const params = new URLSearchParams(location.search);
    const desiredSlug = TAB_SLUGS[currentTab] || TAB_SLUGS[0];
    if (params.get("tab") !== desiredSlug) {
      params.set("tab", desiredSlug);
      const newUrl = `${location.pathname}?${params.toString()}`;
      navigate(newUrl, { replace: true });
    }
  }, [currentTab, navigate, location.pathname, location.search]);

  const tabsConfig = [
    {
      component: EventsTab,
      label: messages["videoMainView_events"],
      index: 0,
      tabName: EVENTS_PREFERENCES_KEY,
    },
    {
      component: ClipsTab,
      label: messages["videoMainView_requested"],
      index: 1,
      tabName: "clipsRequested",
    },
    {
      component: EventsTab,
      label: messages["videoMainView_starred"],
      index: 2,
      tabName: "videoStarred",
    },
    {
      component: EventsTab,
      label: messages["videoMainView_deleted"],
      index: 3,
      tabName: "videoDeleted",
    },
  ];

  return (
    <Container maxWidth={false} className="container-view">
      <Paper elevation={0}>
        {
          <Tabs
            ref={tabsRef}
            tab={currentTab}
            onChange={(event) => {
              if (event === currentTab || loading) return;
              dispatch(clearRedux("AVAILABLE_MEDIA", { events: [] }));
              dispatch(setCurrentTab(event));
            }}
            isLoading={loading}
            delay={DELAY_TAB_CHANGE}
          >
            {tabsConfig.map((tab) => {
              const { component: TabComponent, index, label, tabName } = tab;

              return (
                <TabContainer
                  label={label}
                  index={index}
                  style={{ paddingTop: "15px" }}
                  key={index}
                  tabName={tabName}
                >
                  <TabComponent
                    preferences={preferences}
                    history={history}
                    index={currentTab}
                    tabName={tabName}
                    tableViewRef={tableViewRef}
                  />
                </TabContainer>
              );
            })}
          </Tabs>
        }
      </Paper>
    </Container>
  );
};

export default VideoMainView;
